import React from 'react'
import { PrinterOutlined, PlusOutlined } from '@ant-design/icons'
import { Button, Drawer, Space } from 'antd'
import { useForm } from 'antd/lib/form/Form'
import { useWatch } from 'antd/es/form/Form'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { DeliverySubscriptionType } from '../../../../../types/deliverySubscription/DeliverySubscription'
import { useDeliverySubscriptionSave } from '../../../../../features/deliverySubscriptions/hooks/useDeliverySubscriptionSave'
import { useSubscriptionFormState } from '../../../../../features/deliverySubscriptions/hooks/useSubscriptionFormState'
import CustomReportSubscriptionForm from '../../../../../features/deliverySubscriptions/forms/CustomReportSubscriptionForm'
import { DeliverySubscriptionFormData } from '../../../../../features/deliverySubscriptions/types'
import { authTokenSelector } from '../../../../../redux/session/authentication/selectors'
import { currentUserSortedCompaniesSelector } from '../../../../../redux/session/currentUser/selectors'
import { endOfMonth, now } from '../../../../../config/dayjsConfig'
import useCustomReport from '../../../../../features/customReport/queries/report/useCustomReport'
import { useUpdateCustomReport } from '../../../../../features/customReport/queries/report/useUpdateCustomReport'
import { PrintStyleSettings } from '../../../../../redux/context/customReports/types'
import PrintDrawer from '../../../../reporting/custom/components/print/PrintDrawer'
import DeliverySubscriptionTable from './components/DeliverySubscriptionTable'

function CustomReportsSubscriptions() {
  const { t } = useTranslation()
  const [form] = useForm<DeliverySubscriptionFormData>()
  const { open, deliverySubscription, handleClose, openForEdit, openForCreate } = useSubscriptionFormState(form)

  const { handleSave, isSaving } = useDeliverySubscriptionSave({
    subscriptionType: DeliverySubscriptionType.CUSTOM_REPORT,
    deliverySubscription,
    form,
    onSuccess: handleClose
  })

  const companyId = useWatch('companyId', form)
  const reportId = useWatch('reportId', form)
  const formArgs = useWatch(['params', 'args'], form)
  const formEndDate = useWatch(['params', 'endDate'], form)
  const formPrintVersion = useWatch(['params', 'printVersion'], form)
  const initiatorType = useWatch(['initiator', 'type'], form)
  const previewDate = initiatorType === 'manual' && formEndDate ? formEndDate : endOfMonth(now())

  const companies = useSelector(currentUserSortedCompaniesSelector)
  const selectedCompany = companies.find(c => c.id === companyId)
  const token = useSelector(authTokenSelector)
  const { data: customReport } = useCustomReport(reportId, companyId)
  const { mutateAsync: updateCustomReport, isPending: saveLoading } = useUpdateCustomReport(companyId)

  const handleSavePrintSettings = (settings: PrintStyleSettings) => {
    if (!customReport) return
    updateCustomReport({
      data: { id: customReport.id, style: { ...customReport.style, print: settings } },
      options: { showSuccess: true, skipInvalidation: true }
    })
  }

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
        <Button type="primary" onClick={openForCreate}>
          <PlusOutlined />
          {t('global:add-new')}
        </Button>
      </div>
      <Drawer
        destroyOnHidden
        maskClosable={false}
        rootClassName="delivery-subscription-drawer"
        title={
          deliverySubscription ? t('subscriptionsPage:edit-subscription') : t('subscriptionsPage:add-subscription')
        }
        open={open}
        width={800}
        onClose={handleClose}
        footer={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {customReport &&
            (!deliverySubscription || (formPrintVersion ?? deliverySubscription.params?.printVersion) === 'v2') ? (
              <PrintDrawer
                key={customReport.id}
                report={customReport}
                buttonLabel={t('subscriptionsPage:preview-and-edit-print')}
                companyId={companyId}
                companyName={selectedCompany?.name}
                contractId={selectedCompany?.contract.id}
                token={token}
                date={previewDate}
                args={formArgs ?? deliverySubscription?.params?.args}
                onSavePrintSettings={handleSavePrintSettings}
                saveLoading={saveLoading}
              />
            ) : (
              <Button icon={<PrinterOutlined />} disabled>
                {t('subscriptionsPage:preview-and-edit-print')}
              </Button>
            )}
            <Space>
              <Button onClick={handleClose}>{t('global:cancel')}</Button>
              <Button type="primary" loading={isSaving} onClick={handleSave}>
                {deliverySubscription ? t('global:update') : t('global:save')}
              </Button>
            </Space>
          </div>
        }
      >
        <CustomReportSubscriptionForm form={form} deliverySubscription={deliverySubscription} />
      </Drawer>
      <DeliverySubscriptionTable
        subscriptionType={DeliverySubscriptionType.CUSTOM_REPORT}
        toggleDeliverySubscription={openForEdit}
      />
    </>
  )
}

export default CustomReportsSubscriptions
